import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CreditCard, Check, X, Euro, Calendar, User, Mail, FileText, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentsManagerEnhanced = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/payments`, getAuthHeaders());
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Payé', class: 'bg-green-100 text-green-800' },
      pending: { label: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
      failed: { label: 'Échoué', class: 'bg-red-100 text-red-800' },
      refunded: { label: 'Remboursé', class: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      card: 'Carte bancaire',
      paypal: 'PayPal',
      stripe: 'Stripe',
      bank_transfer: 'Virement',
      cash: 'Espèces'
    };
    return methods[method] || method;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const filteredPayments = statusFilter === 'all' 
    ? payments 
    : payments.filter(p => p.status === statusFilter);

  const stats = {
    total: payments.length,
    completed: payments.filter(p => p.status === 'completed').length,
    pending: payments.filter(p => p.status === 'pending').length,
    totalAmount: payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Gestion des Paiements</h1>
        <p className="text-slate-600">Consultez les détails des transactions</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total transactions</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Payés</p>
                <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <Check className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">En attente</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <X className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Montant total</p>
                <p className="text-2xl font-bold text-blue-900">{formatPrice(stats.totalAmount)}</p>
              </div>
              <Euro className="h-8 w-8 text-blue-900" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Filtrer par statut :</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="completed">Payés</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoués</option>
              <option value="refunded">Remboursés</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des paiements ({filteredPayments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Chargement...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Aucun paiement trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Méthode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Détails
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-slate-900">{payment.user_name}</div>
                          <div className="text-sm text-slate-500">{payment.user_email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">{payment.service_plan}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-900">
                            {getPaymentMethodLabel(payment.payment_method)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {payment.payment_method === 'stripe' && payment.card_last4 && (
                          <div className="text-sm text-slate-600">
                            <div className="font-mono">•••• {payment.card_last4}</div>
                            {payment.card_brand && (
                              <div className="text-xs text-slate-500">{payment.card_brand}</div>
                            )}
                          </div>
                        )}
                        {payment.payment_method === 'paypal' && (
                          <div className="text-sm text-slate-600">
                            {payment.paypal_payer_email ? (
                              <div className="text-xs">{payment.paypal_payer_email}</div>
                            ) : (
                              <span>PayPal</span>
                            )}
                          </div>
                        )}
                        {!payment.card_last4 && payment.payment_method !== 'paypal' && (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{formatPrice(payment.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">
                          {formatDate(payment.payment_date || payment.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowDetails(true);
                          }}
                        >
                          <Info className="h-4 w-4 mr-1" />
                          Détails
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      {showDetails && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Détails de la transaction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informations client
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Nom :</span>
                    <span className="font-medium">{selectedPayment.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Email :</span>
                    <span className="font-medium">{selectedPayment.user_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">ID utilisateur :</span>
                    <span className="font-mono text-xs">{selectedPayment.user_id}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Informations de paiement
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Méthode :</span>
                    <span className="font-medium">{getPaymentMethodLabel(selectedPayment.payment_method)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Montant :</span>
                    <span className="font-bold text-lg">{formatPrice(selectedPayment.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Statut :</span>
                    {getStatusBadge(selectedPayment.status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Transaction ID :</span>
                    <span className="font-mono text-xs">{selectedPayment.transaction_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Date de paiement :</span>
                    <span>{formatDate(selectedPayment.payment_date)}</span>
                  </div>
                </div>
              </div>

              {/* Card Details */}
              {selectedPayment.card_last4 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-3">Détails de la carte</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">4 derniers chiffres :</span>
                      <span className="font-mono font-bold">•••• {selectedPayment.card_last4}</span>
                    </div>
                    {selectedPayment.card_brand && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Type de carte :</span>
                        <span className="font-medium">{selectedPayment.card_brand}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PayPal Details */}
              {selectedPayment.payment_method === 'paypal' && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-3">Détails PayPal</h3>
                  <div className="space-y-2 text-sm">
                    {selectedPayment.paypal_payer_email && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Email PayPal :</span>
                        <span className="font-medium">{selectedPayment.paypal_payer_email}</span>
                      </div>
                    )}
                    {selectedPayment.paypal_payer_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Nom PayPal :</span>
                        <span className="font-medium">{selectedPayment.paypal_payer_name}</span>
                      </div>
                    )}
                    {selectedPayment.paypal_transaction_details && (
                      <div className="mt-3">
                        <span className="text-slate-600 block mb-2">Logs de transaction :</span>
                        <pre className="bg-slate-50 p-3 rounded text-xs overflow-x-auto">
                          {JSON.stringify(selectedPayment.paypal_transaction_details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedPayment.notes && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-3">Notes</h3>
                  <p className="text-sm text-slate-600">{selectedPayment.notes}</p>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedPayment(null);
                  }}
                  variant="outline"
                >
                  Fermer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PaymentsManagerEnhanced;
